import { GameState, TeamInLeague, TrainingFocus, Position } from '../domain/entities';
import { TRAINING_FACILITIES_LEVELS, YOUTH_ACADEMY_LEVELS, ASSISTANT_MANAGER_LEVELS } from '../domain/constants';

/**
 * AIFacilityManagementService
 * Handles facility upgrades and training focus decisions for AI teams
 */
export const AIFacilityManagementService = {
    /**
     * Process AI team facility upgrades and training focus decisions for the season
     */
    processAITeamUpgrades: (gameState: GameState): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;

        // Process each league
        Object.values(newState.leagues).forEach(league => {
            league.teams.forEach(team => {
                // Skip player's team
                if (team.name === newState.playerTeamName) return;

                // Evaluate and execute facility upgrades
                AIFacilityManagementService.evaluateAndExecuteUpgrades(team);

                // Set training focuses for the team
                AIFacilityManagementService.assignTrainingFocuses(team);
            });
        });

        return newState;
    },

    /**
     * Evaluate whether to upgrade facilities based on financial situation and team needs
     */
    evaluateAndExecuteUpgrades: (team: TeamInLeague): void => {
        const balance = team.finances.balance;

        // Calculate average player age to inform investment decisions
        const avgAge = team.squad.length > 0
            ? team.squad.reduce((sum, p) => sum + p.age, 0) / team.squad.length
            : 25;

        const hasYoungSquad = avgAge < 26;

        // Determine upgrade priority based on current state and needs
        let trainingLevel = team.trainingFacilities.level;
        let youthLevel = team.youthAcademy.level;
        let assistantLevel = team.assistantManager.level;

        // Calculate minimum balance to maintain for weekly operations
        const calculateMinBalance = () => {
            const weeklyWageCost = team.squad.reduce((sum, p) => sum + p.wage, 0) +
                (TRAINING_FACILITIES_LEVELS[trainingLevel - 1]?.weeklyWages || 0) +
                (YOUTH_ACADEMY_LEVELS[youthLevel - 1]?.weeklyWages || 0) +
                (ASSISTANT_MANAGER_LEVELS[assistantLevel - 1]?.weeklyWages || 0);
            return weeklyWageCost * 4; // Need at least 4 weeks of operational expenses
        };

        // Aggressive upgrade loop - keep upgrading all facilities while we have excess cash
        // Cycles through facilities multiple times until none can be upgraded
        let maxCycles = 6; // Max 6 cycles through all 3 facilities = up to 18 upgrades
        let cyclesCompleted = 0;

        while (cyclesCompleted < maxCycles && team.finances.balance > calculateMinBalance() + 500000) {
            let anyUpgradeThisCycle = false;
            const minBalance = calculateMinBalance();

            // Priority 1: Assistant Manager (improves transfers/decisions)
            if (assistantLevel < 6) {
                const upgradeCost = ASSISTANT_MANAGER_LEVELS[assistantLevel]?.upgradeCost || 150000;
                if (team.finances.balance > minBalance + upgradeCost) {
                    team.assistantManager.level = Math.min(6, assistantLevel + 1);
                    team.finances.balance -= upgradeCost;
                    team.finances.history.push({
                        season: team.finances.history[team.finances.history.length - 1]?.season || 1,
                        week: team.finances.history[team.finances.history.length - 1]?.week || 1,
                        event: `Upgraded Assistant Manager to Level ${team.assistantManager.level}`,
                        amount: -upgradeCost
                    });
                    assistantLevel = team.assistantManager.level;
                    anyUpgradeThisCycle = true;
                }
            }

            // Priority 2: Training Facilities (improves player performance)
            if (trainingLevel < 6) {
                const minBalanceAfterAssistant = team.squad.reduce((sum, p) => sum + p.wage, 0) +
                    (TRAINING_FACILITIES_LEVELS[trainingLevel - 1]?.weeklyWages || 0) +
                    (YOUTH_ACADEMY_LEVELS[youthLevel - 1]?.weeklyWages || 0) +
                    (ASSISTANT_MANAGER_LEVELS[assistantLevel - 1]?.weeklyWages || 0);
                const upgradeCost = TRAINING_FACILITIES_LEVELS[trainingLevel]?.upgradeCost || 100000;
                if (team.finances.balance > minBalanceAfterAssistant * 4 + upgradeCost) {
                    team.trainingFacilities.level = Math.min(6, trainingLevel + 1);
                    team.finances.balance -= upgradeCost;
                    team.finances.history.push({
                        season: team.finances.history[team.finances.history.length - 1]?.season || 1,
                        week: team.finances.history[team.finances.history.length - 1]?.week || 1,
                        event: `Upgraded Training Facilities to Level ${team.trainingFacilities.level}`,
                        amount: -upgradeCost
                    });
                    trainingLevel = team.trainingFacilities.level;
                    anyUpgradeThisCycle = true;
                }
            }

            // Priority 3: Youth Academy (invest in young squad development)
            if (youthLevel < 6 && hasYoungSquad) {
                const minBalanceAfterFacilities = team.squad.reduce((sum, p) => sum + p.wage, 0) +
                    (TRAINING_FACILITIES_LEVELS[trainingLevel - 1]?.weeklyWages || 0) +
                    (YOUTH_ACADEMY_LEVELS[youthLevel - 1]?.weeklyWages || 0) +
                    (ASSISTANT_MANAGER_LEVELS[assistantLevel - 1]?.weeklyWages || 0);
                const upgradeCost = YOUTH_ACADEMY_LEVELS[youthLevel]?.upgradeCost || 250000;
                if (team.finances.balance > minBalanceAfterFacilities * 4 + upgradeCost) {
                    team.youthAcademy.level = Math.min(6, youthLevel + 1);
                    team.finances.balance -= upgradeCost;
                    team.finances.history.push({
                        season: team.finances.history[team.finances.history.length - 1]?.season || 1,
                        week: team.finances.history[team.finances.history.length - 1]?.week || 1,
                        event: `Upgraded Youth Academy to Level ${team.youthAcademy.level}`,
                        amount: -upgradeCost
                    });
                    youthLevel = team.youthAcademy.level;
                    anyUpgradeThisCycle = true;
                }
            }

            // If we couldn't upgrade any facility this cycle, stop trying
            if (!anyUpgradeThisCycle) {
                break;
            }

            cyclesCompleted++;
        }

        // Handle financial difficulties
        const minBalance = calculateMinBalance();
        if (team.finances.balance < minBalance) {
            // We need cash - consider downgrading facilities

            // Downgrade Youth Academy first (least impactful)
            if (youthLevel > 1) {
                const downgradeSavings = (YOUTH_ACADEMY_LEVELS[youthLevel - 2]?.weeklyWages || 0) -
                    (YOUTH_ACADEMY_LEVELS[youthLevel - 1]?.weeklyWages || 0);
                const refund = (YOUTH_ACADEMY_LEVELS[youthLevel - 1]?.upgradeCost || 250000) * 0.5; // 50% refund

                if (downgradeSavings > 0 || team.finances.balance < 0) {
                    team.youthAcademy.level = Math.max(1, youthLevel - 1);
                    team.finances.balance += refund;
                    team.finances.history.push({
                        season: team.finances.history[team.finances.history.length - 1]?.season || 1,
                        week: team.finances.history[team.finances.history.length - 1]?.week || 1,
                        event: `Downgraded Youth Academy to Level ${team.youthAcademy.level}`,
                        amount: refund
                    });
                    return;
                }
            }

            // Downgrade Training Facilities if still in deficit
            if (trainingLevel > 1 && team.finances.balance < minBalance * 0.5) {
                const refund = (TRAINING_FACILITIES_LEVELS[trainingLevel - 1]?.upgradeCost || 100000) * 0.5;
                team.trainingFacilities.level = Math.max(1, trainingLevel - 1);
                team.finances.balance += refund;
                team.finances.history.push({
                    season: team.finances.history[team.finances.history.length - 1]?.season || 1,
                    week: team.finances.history[team.finances.history.length - 1]?.week || 1,
                    event: `Downgraded Training Facilities to Level ${team.trainingFacilities.level}`,
                    amount: refund
                });
                return;
            }
        }
    },

    /**
     * Assign training focuses for AI teams based on squad composition
     */
    assignTrainingFocuses: (team: TeamInLeague): void => {
        // Skip if already assigned (only do this once per team typically)
        const hasAnyFocus = team.trainingFocus !== 'General' ||
                           Object.values(team.positionalTrainingFocus).some(f => f !== null && f !== 'General');
        if (hasAnyFocus && team.trainingFocus !== 'General') {
            return; // Already configured
        }

        // Analyze squad composition
        const positions: Record<Position, number> = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
        const avgSkills: Record<Position, number> = { GK: 0, DEF: 0, MID: 0, FWD: 0 };

        team.squad.forEach(p => {
            positions[p.position]++;
            avgSkills[p.position] += p.skill;
        });

        Object.keys(positions).forEach(pos => {
            if (positions[pos as Position] > 0) {
                avgSkills[pos as Position] /= positions[pos as Position];
            }
        });

        // Determine team's overall strategic needs
        const defenseStrength = (avgSkills.GK + avgSkills.DEF) / 2;
        const midfieldStrength = avgSkills.MID;
        const attackStrength = avgSkills.FWD;

        // Set team-wide training focus based on weakest area
        if (defenseStrength < midfieldStrength && defenseStrength < attackStrength) {
            team.trainingFocus = 'Defending';
        } else if (attackStrength < midfieldStrength && attackStrength < defenseStrength) {
            team.trainingFocus = 'Attacking';
        } else if (midfieldStrength < defenseStrength && midfieldStrength < attackStrength) {
            team.trainingFocus = 'Midfield';
        } else {
            team.trainingFocus = 'General';
        }

        // Set positional training focuses
        // Goalkeepers: Goalkeeping if below average
        if (avgSkills.GK > 0 && avgSkills.GK < 55) {
            team.positionalTrainingFocus.GK = 'Goalkeeping';
        } else {
            team.positionalTrainingFocus.GK = null;
        }

        // Defenders: Defending if below average
        if (avgSkills.DEF > 0 && avgSkills.DEF < 55) {
            team.positionalTrainingFocus.DEF = 'Defending';
        } else {
            team.positionalTrainingFocus.DEF = null;
        }

        // Midfielders: Midfield if below average
        if (avgSkills.MID > 0 && avgSkills.MID < 55) {
            team.positionalTrainingFocus.MID = 'Midfield';
        } else {
            team.positionalTrainingFocus.MID = null;
        }

        // Forwards: Attacking if below average
        if (avgSkills.FWD > 0 && avgSkills.FWD < 55) {
            team.positionalTrainingFocus.FWD = 'Attacking';
        } else {
            team.positionalTrainingFocus.FWD = null;
        }
    }
};
